import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Navigation from './Navigation';
import apiService from '../services/api';
import { ALL_BRANDS, FUEL_TYPES, BODY_TYPES, SELLER_TYPES } from '../constants/carFilters';
import { composeSms, applyTemplate, num, postcode } from '../utils/smsFormat';
import Spinner from './Spinner';
import Toast from './Toast';

// Privzete meje veljajo za prehod (Telemach). Pri lastni SIM (nacin "naprava") API v profilu
// vrne vecje meje (500/600); brez tega bi bil dolg SMS (npr. PAA Global) v vnosu odrezan.
const MAX_MESSAGE_DEFAULT = 100;
const MAX_SMS_DEFAULT = 160;

const PACKAGES = [
  { key: 'test', name: 'Testni paket', price: 'Brezplačno', count: '10 SMS', perSms: 'za preizkus' },
  { key: 'mini', name: 'Mini paket', price: '75 €', count: '500 SMS', perSms: '0,15 € / SMS' },
  { key: 'maxi', name: 'Maxi paket', price: '120 €', count: '1200 SMS', perSms: '0,10 € / SMS' },
];

const REGIONS = [
  { key: 'all', label: 'Vse lokacije', min: 0, max: 9999 },
  { key: '1', label: '1000–1999 (Ljubljana z okolico)', min: 1000, max: 1999 },
  { key: '2', label: '2000–2999 (Maribor, Ptuj …)', min: 2000, max: 2999 },
  { key: '3', label: '3000–3999 (Celje …)', min: 3000, max: 3999 },
  { key: '4', label: '4000–4999 (Kranj, Gorenjska)', min: 4000, max: 4999 },
  { key: '5', label: '5000–5999 (Nova Gorica, Primorska)', min: 5000, max: 5999 },
  { key: '6', label: '6000–6999 (Koper, Postojna)', min: 6000, max: 6999 },
  { key: '8', label: '8000–8999 (Novo mesto, Dolenjska)', min: 8000, max: 8999 },
  { key: '9', label: '9000–9999 (Murska Sobota, Prekmurje)', min: 9000, max: 9999 },
];

const inputStyle = { padding: 7, boxSizing: 'border-box' };
const cardStyle = { border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 16, background: 'white' };

// Kanonična tel. številka za ujemanje (dedup + blok) — zrcali backend SmsRules.normalizePhone.
function normalizePhone(p) {
  if (!p) return '';
  let d = String(p).replace(/[^0-9+]/g, '');
  if (d.startsWith('+')) d = d.slice(1);
  if (d.startsWith('00')) d = d.slice(2);
  d = d.replace(/[^0-9]/g, '');
  if (!d) return '';
  if (d.startsWith('386')) return d;
  if (d.startsWith('0')) return '386' + d.slice(1);
  return d;
}

function SmsBoard({ embedded }) {
  const [profile, setProfile] = useState({ companyName: '', contactPhone: '', smsPackage: 'mini', defaultSmsMessage: '', autoSmsEnabled: false, damageSmsMessage: '', firstOwnerSmsMessage: '', autoSmsDamage: false, autoSmsFirstOwner: false, smsDedupEnabled: true, smsBlockList: '' });
  const [profileStatus, setProfileStatus] = useState('');
  const [sentSet, setSentSet] = useState(new Map()); // normaliziran tel -> datum prvega pošiljanja
  const [cars, setCars] = useState([]);
  const [damagedCars, setDamagedCars] = useState([]);
  const [firstOwnerCars, setFirstOwnerCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);

  const [messages, setMessages] = useState({});
  const [sendStatus, setSendStatus] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [selResult, setSelResult] = useState(null);

  const [mode, setMode] = useState('manual');
  const [autoTarget, setAutoTarget] = useState('all');
  const [smsCategory, setSmsCategory] = useState('avtomobili'); // 'avtomobili' | 'okvari' | 'lastnik'
  const [filters, setFilters] = useState({
    brands: [], fuels: [], bodies: [], sellers: [],
    letnikMin: '', letnikMax: '', cenaMin: '', cenaMax: '', kmMin: '', kmMax: '', region: 'all',
  });
  const [filterSaved, setFilterSaved] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');
  const [search, setSearch] = useState('');       // manual list quick search
  const [sortBy, setSortBy] = useState('znamka');  // manual list sort
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);

  const [testPhone, setTestPhone] = useState('');
  const [testStatus, setTestStatus] = useState('');
  const [toast, setToast] = useState(null);

  const [history, setHistory] = useState([]);

  const loadUsage = useCallback(async () => {
    try {
      const res = await apiService.getSmsUsage();
      if (res && res.data) setUsage(res.data);
    } catch (e) { /* non-critical */ }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await apiService.getSmsHistory();
      if (res && res.data) setHistory(res.data);
    } catch (e) { /* non-critical */ }
  }, []);

  const refreshStats = useCallback(() => { loadUsage(); loadHistory(); }, [loadUsage, loadHistory]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, carsRes, damagedRes, firstOwnerRes, sentRes] = await Promise.all([
        apiService.getSmsProfile(),
        apiService.getRecentCars(24),
        apiService.getDamagedCars(168),
        apiService.getFirstOwnerCars(168),
        apiService.getSmsSentRecipients(),
      ]);
      if (profileRes && profileRes.data) {
        setProfile({
          companyName: profileRes.data.companyName || '',
          contactPhone: profileRes.data.contactPhone || '',
          smsPackage: profileRes.data.smsPackage || 'mini',
          defaultSmsMessage: profileRes.data.defaultSmsMessage || '',
          autoSmsEnabled: !!profileRes.data.autoSmsEnabled,
          damageSmsMessage: profileRes.data.damageSmsMessage || '',
          firstOwnerSmsMessage: profileRes.data.firstOwnerSmsMessage || '',
          autoSmsDamage: !!profileRes.data.autoSmsDamage,
          autoSmsFirstOwner: !!profileRes.data.autoSmsFirstOwner,
          smsDedupEnabled: profileRes.data.smsDedupEnabled !== false, // privzeto ON
          smsBlockList: profileRes.data.smsBlockList || '',
          maxMessageLength: Number(profileRes.data.maxMessageLength) || MAX_MESSAGE_DEFAULT,
          maxSmsLength: Number(profileRes.data.maxSmsLength) || MAX_SMS_DEFAULT,
        });
      }
      const withPhone = (r) => (((r && r.data) || []).filter((c) => c.phone && c.phone.trim()));
      setCars(withPhone(carsRes));
      setDamagedCars(withPhone(damagedRes));
      setFirstOwnerCars(withPhone(firstOwnerRes));
      // Normaliziran prejemnik -> datum prvega pošiljanja (za opozorilo "že poslano").
      const sm = new Map();
      ((sentRes && sentRes.data) || []).forEach((s) => { if (s.phone) sm.set(s.phone, s.firstSentAt); });
      setSentSet(sm);
    } catch (e) {
      console.error('Napaka pri nalaganju SMS plošče:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); refreshStats(); }, [loadData, refreshStats]);

  const intOrNull = (v) => { const n = parseInt(v, 10); return isNaN(n) ? null : n; };

  // Load the saved profile filter on mount and reflect it on the SMS board.
  useEffect(() => {
    apiService.getUserFilter().then((res) => {
      const d = res.data || {};
      const has = (d.brands && d.brands.length) || (d.fuelTypes && d.fuelTypes.length)
        || (d.bodyTypes && d.bodyTypes.length) || (d.sellerTypes && d.sellerTypes.length)
        || d.minYear != null || d.maxYear != null || d.minPrice != null || d.maxPrice != null
        || d.minMileage != null || d.maxMileage != null;
      setFilters({
        brands: d.brands || [], fuels: d.fuelTypes || [], bodies: d.bodyTypes || [], sellers: d.sellerTypes || [],
        letnikMin: d.minYear != null ? String(d.minYear) : '', letnikMax: d.maxYear != null ? String(d.maxYear) : '',
        cenaMin: d.minPrice != null ? String(d.minPrice) : '', cenaMax: d.maxPrice != null ? String(d.maxPrice) : '',
        kmMin: d.minMileage != null ? String(d.minMileage) : '', kmMax: d.maxMileage != null ? String(d.maxMileage) : '',
        region: 'all',
      });
      if (has) setAutoTarget('filter');
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Potrdi filter" — persist the current filter to the user's profile (auto-SMS uses it too).
  const saveSmsFilter = async () => {
    const f = filters;
    try {
      await apiService.updateUserFilter({
        brands: f.brands, fuelTypes: f.fuels, bodyTypes: f.bodies, sellerTypes: f.sellers,
        minYear: intOrNull(f.letnikMin), maxYear: intOrNull(f.letnikMax),
        minPrice: intOrNull(f.cenaMin), maxPrice: intOrNull(f.cenaMax),
        minMileage: intOrNull(f.kmMin), maxMileage: intOrNull(f.kmMax),
        model: null, ageTypes: [], active: true,
      });
      setFilterSaved(true);
      setTimeout(() => setFilterSaved(false), 2500);
    } catch (e) {
      alert('Napaka pri shranjevanju filtra. Poskusite znova.');
    }
  };

  // "Razveljavi filter" — clear the saved filter so all cars show again.
  const clearSmsFilter = async () => {
    setFilters({ brands: [], fuels: [], bodies: [], sellers: [], letnikMin: '', letnikMax: '', cenaMin: '', cenaMax: '', kmMin: '', kmMax: '', region: 'all' });
    setAutoTarget('all');
    try {
      await apiService.updateUserFilter({
        brands: [], fuelTypes: [], bodyTypes: [], sellerTypes: [],
        minYear: null, maxYear: null, minPrice: null, maxPrice: null, minMileage: null, maxMileage: null,
        model: null, ageTypes: [], active: false,
      });
    } catch (e) { /* non-critical */ }
  };

  const saveProfile = async () => {
    setProfileStatus('Shranjujem ...');
    try {
      await apiService.updateSmsProfile(profile);
      setProfileStatus('✓ Shranjeno');
    } catch (e) {
      setProfileStatus('✗ Napaka');
    }
    setTimeout(() => setProfileStatus(''), 3000);
  };

  const toggleAuto = async (field, label) => {
    const next = !profile[field];
    const updated = { ...profile, [field]: next };
    setProfile(updated);
    try {
      await apiService.updateSmsProfile(updated);
      setToast({ type: next ? 'success' : 'info', message: `${label}: samodejno ${next ? 'VKLOPLJENO' : 'izklopljeno'}` });
    } catch (e) {
      setProfile((p) => ({ ...p, [field]: !next })); // revert on error
      setToast({ type: 'error', message: 'Napaka pri shranjevanju.' });
    }
  };

  // Shrani blok-listo (ob izgubi fokusa textarea).
  const saveBlockList = async () => {
    try {
      await apiService.updateSmsProfile(profile);
      setToast({ type: 'success', message: 'Blok-lista shranjena.' });
    } catch (e) {
      setToast({ type: 'error', message: 'Napaka pri shranjevanju blok-liste.' });
    }
  };

  // Normalizirane številke z uporabnikove blok-liste (opozorilo + preskok pošiljanja).
  const blockedSet = useMemo(() => {
    const set = new Set();
    (profile.smsBlockList || '').split(/\r?\n/).forEach((line) => { const n = normalizePhone(line); if (n) set.add(n); });
    return set;
  }, [profile.smsBlockList]);

  // Status prejemnika za oglas: { kind: 'blocked' } | { kind: 'sent', date } | null.
  const phoneStatus = (phone) => {
    const key = normalizePhone(phone);
    if (!key) return null;
    if (blockedSet.has(key)) return { kind: 'blocked' };
    if (sentSet.has(key)) return { kind: 'sent', date: sentSet.get(key) };
    return null;
  };

  const visibleBrands = useMemo(
    () => ALL_BRANDS.filter((b) => b.toLowerCase().includes(brandSearch.toLowerCase())),
    [brandSearch]
  );

  // Manual list: quick search (brand/model/phone) + sort.
  const displayedCars = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = cars;
    if (q) {
      list = list.filter((c) =>
        `${c.znamka || ''} ${c.model || ''} ${c.phone || ''}`.toLowerCase().includes(q));
    }
    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sortBy === 'cena') return (num(a.cena) || 0) - (num(b.cena) || 0);
      if (sortBy === 'letnik') return (num(b.letnik) || 0) - (num(a.letnik) || 0); // novejši najprej
      return `${a.znamka || ''} ${a.model || ''}`.localeCompare(`${b.znamka || ''} ${b.model || ''}`);
    });
    return sorted;
  }, [cars, search, sortBy]);

  // Vir za masovno pošiljanje po izbrani kategoriji + pripadajoče privzeto sporočilo.
  const sourceCars = useMemo(
    () => (smsCategory === 'okvari' ? damagedCars : smsCategory === 'lastnik' ? firstOwnerCars : cars),
    [smsCategory, cars, damagedCars, firstOwnerCars]
  );
  const categoryMessage = smsCategory === 'okvari' ? profile.damageSmsMessage
    : smsCategory === 'lastnik' ? profile.firstOwnerSmsMessage : profile.defaultSmsMessage;

  const filteredCars = useMemo(() => {
    if (autoTarget === 'all') return sourceCars;
    const region = REGIONS.find((r) => r.key === filters.region) || REGIONS[0];
    const between = (v, lo, hi) =>
      (lo === '' || (v != null && v >= num(lo))) && (hi === '' || (v != null && v <= num(hi)));
    return sourceCars.filter((c) => {
      if (filters.brands.length && !filters.brands.includes(c.znamka)) return false;
      if (filters.fuels.length && !filters.fuels.includes(c.gorivo)) return false;
      if (filters.bodies.length && !filters.bodies.includes(c.karoserija)) return false;
      if (filters.sellers.length && !filters.sellers.includes(c.sellerStatus)) return false;
      if (!between(num(c.letnik), filters.letnikMin, filters.letnikMax)) return false;
      if (!between(num(c.cena), filters.cenaMin, filters.cenaMax)) return false;
      if (!between(num(c.mileage), filters.kmMin, filters.kmMax)) return false;
      const pc = postcode(c.viewLocation);
      if (pc == null || pc < region.min || pc > region.max) return false;
      return true;
    });
  }, [sourceCars, autoTarget, filters]);

  const statusText = (d) => {
    if (!d) return '';
    if (d.sent) return '✓ Poslano';
    if (d.status === 'DISABLED') return '⚠ ' + (d.note || 'Prehod izklopljen (zabeleženo, ni poslano).');
    return '⚠ ' + (d.error || d.status || 'Ni poslano');
  };

  const handleSend = async (car) => {
    const raw = (messages[car.id] !== undefined ? messages[car.id] : (profile.defaultSmsMessage || '')).trim();
    const message = applyTemplate(raw, car); // resolve {znamka} {model} … for this car
    if (!message) {
      setSendStatus((s) => ({ ...s, [car.id]: { type: 'error', text: 'Sporočilo je prazno.' } }));
      return;
    }
    setSendStatus((s) => ({ ...s, [car.id]: { type: 'sending', text: 'Pošiljam ...' } }));
    try {
      const res = await apiService.sendSms({ avtonetId: car.avtonetId, recipientPhone: car.phone, message, filterUsed: 'Ročno' });
      setSendStatus((s) => ({ ...s, [car.id]: { type: res.data && res.data.sent ? 'ok' : 'warn', text: statusText(res.data) } }));
      refreshStats();
    } catch (e) {
      const msg = (e.response && e.response.data && e.response.data.error) || 'Napaka';
      setSendStatus((s) => ({ ...s, [car.id]: { type: 'error', text: '✗ ' + msg } }));
      setToast({ type: 'error', message: msg });
    }
  };

  const handleTestSend = async () => {
    const message = (profile.defaultSmsMessage || 'AvtoRadar test SMS.').trim();
    if (!testPhone.trim()) { setTestStatus('Vpiši testno številko.'); return; }
    setTestStatus('Pošiljam ...');
    try {
      const res = await apiService.sendSms({ recipientPhone: testPhone.trim(), message });
      setTestStatus(statusText(res.data));
      refreshStats();
    } catch (e) {
      const msg = (e.response && e.response.data && e.response.data.error) || 'Napaka';
      setTestStatus('✗ ' + msg);
      setToast({ type: 'error', message: msg });
    }
  };

  // carList → resolves the default template per car and bulk-sends.
  const runBulk = async (carList, filterUsed, setResult, templateOverride) => {
    const template = ((templateOverride != null ? templateOverride : profile.defaultSmsMessage) || '').trim();
    if (!template) { setResult({ error: 'Najprej nastavi privzeto sporočilo za to kategorijo (profil zgoraj).' }); return; }
    if (!carList.length) { setResult({ error: 'Ni prejemnikov.' }); return; }
    if (!window.confirm(`Poslati SMS ${carList.length} prejemnikom?`)) return;
    setResult({ sending: true });
    try {
      const recipients = carList.map((c) => ({ avtonetId: c.avtonetId, phone: c.phone, message: applyTemplate(template, c) }));
      const res = await apiService.sendSmsBulk({ recipients, filterUsed });
      setResult(res.data);
      if (res.data && typeof res.data.sent === 'number') {
        setToast({ type: res.data.failed ? 'info' : 'success', message: `Poslano ${res.data.sent} · napak ${res.data.failed}` });
      }
      refreshStats();
    } catch (e) {
      const msg = (e.response && e.response.data && e.response.data.error) || 'Napaka pri pošiljanju.';
      setResult({ error: msg });
      setToast({ type: 'error', message: msg });
    }
  };

  // Human-readable description of the active filter (stored as evidence).
  const describeFilter = () => {
    const parts = [];
    if (filters.brands.length) parts.push('Znamke: ' + filters.brands.join(', '));
    if (filters.fuels.length) parts.push('Gorivo: ' + filters.fuels.map((c) => FUEL_TYPES[c] || c).join(', '));
    if (filters.bodies.length) parts.push('Karoserija: ' + filters.bodies.map((c) => BODY_TYPES[c] || c).join(', '));
    if (filters.sellers.length) parts.push('Prodajalec: ' + filters.sellers.join(', '));
    if (filters.letnikMin || filters.letnikMax) parts.push(`Letnik: ${filters.letnikMin || ''}–${filters.letnikMax || ''}`);
    if (filters.cenaMin || filters.cenaMax) parts.push(`Cena: ${filters.cenaMin || ''}–${filters.cenaMax || ''}`);
    if (filters.kmMin || filters.kmMax) parts.push(`Km: ${filters.kmMin || ''}–${filters.kmMax || ''}`);
    if (filters.region && filters.region !== 'all') {
      const r = REGIONS.find((x) => x.key === filters.region);
      parts.push('Lokacija: ' + (r ? r.label : filters.region));
    }
    return parts.length ? 'Filter — ' + parts.join(' · ') : 'Po filtru (brez omejitev)';
  };

  const handleBulkSend = async () => {
    setBulkSending(true);
    const catLabel = smsCategory === 'okvari' ? 'V okvari' : smsCategory === 'lastnik' ? '1. lastnik' : 'Avtomobili';
    const filterUsed = catLabel + ' — ' + (autoTarget === 'all' ? 'vsi' : describeFilter());
    await runBulk(filteredCars, filterUsed, setBulkResult, categoryMessage);
    setBulkSending(false);
  };

  const handleSelectedSend = async () => {
    const chosen = cars.filter((c) => selected.has(c.id));
    await runBulk(chosen, `Izbrani (${chosen.length})`, (r) => {
      setSelResult(r);
      if (r && !r.error && !r.sending) setSelected(new Set());
    });
  };

  const toggleSelect = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleIn = (key, value) => setFilters((f) => ({
    ...f, [key]: f[key].includes(value) ? f[key].filter((x) => x !== value) : [...f[key], value],
  }));

  const bulkSummary = (r) => {
    if (!r) return null;
    if (r.sending) return <span>Pošiljam ...</span>;
    if (r.error) return <span style={{ color: '#d00' }}>✗ {r.error}</span>;
    return (
      <span style={{ color: '#080' }}>
        poslano {r.sent} · napak {r.failed}
        {r.skippedLimit ? ` · čez dnevni limit ${r.skippedLimit}` : ''}
        {!r.enabled ? ' (prehod izklopljen – ni poslano)' : ''}
      </span>
    );
  };

  return (
    <div>
      {!embedded && <Navigation />}
      <div className="sms-board" style={{ maxWidth: 1100, margin: '0 auto', padding: embedded ? '0' : '20px' }}>
        <h2 style={embedded ? { marginTop: 0 } : undefined}>SMS plošča</h2>

        {usage ? (
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontWeight: 600 }}>Poraba paketa ({usage.smsPackage === 'maxi' ? 'Maxi' : 'Mini'}) – ta mesec</span>
              <span>Porabljeno <b>{usage.used}</b> / {usage.quota} · Preostane <b>{usage.remaining}</b></span>
            </div>
            <div style={{ height: 10, background: '#eee', borderRadius: 5, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.min(100, usage.quota ? (usage.used / usage.quota) * 100 : 0)}%`, background: usage.remaining <= 0 ? '#d00' : '#007AFF' }}></div>
            </div>
            {usage.dailyLimit != null ? (
              <div style={{ marginTop: 8, fontSize: 13, color: usage.remainingToday <= 0 ? '#d00' : '#555' }}>
                Dnevni limit: porabljeno <b>{usage.usedToday}</b> / {usage.dailyLimit} · danes preostane <b>{usage.remainingToday}</b>
              </div>
            ) : null}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          {PACKAGES.map((p) => (
            <div key={p.key} style={{ flex: '1 1 220px', border: '1px solid #ddd', borderRadius: 8, padding: 16, background: profile.smsPackage === p.key ? '#eef5ff' : 'white' }}>
              <div style={{ fontWeight: 700 }}>{p.name}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#007AFF', margin: '6px 0' }}>{p.price}</div>
              <div style={{ color: '#555' }}>{p.count}</div>
              <div style={{ color: '#555' }}>{p.perSms}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: -4, marginBottom: 16, color: '#555', fontSize: 14 }}>
          Za <strong>spremembo paketa</strong> nas pokličite na <a href="tel:+38631324100" style={{ color: '#007AFF', fontWeight: 600 }}>031 324 100</a>.
        </div>

        <div style={{ ...cardStyle, background: '#fafafa' }}>
          <div style={{ fontWeight: 600, marginBottom: 10 }}>Moja avtohiša (vključeno v vsak SMS)</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>Ime avtohiše (do 50)
              <input value={profile.companyName} maxLength={50} onChange={(e) => setProfile({ ...profile, companyName: e.target.value })} placeholder="Avtohiša Primer" style={{ ...inputStyle, minWidth: 220 }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>Moj telefon (10 znakov)
              <input value={profile.contactPhone} maxLength={10} onChange={(e) => setProfile({ ...profile, contactPhone: e.target.value })} placeholder="041123456" style={{ ...inputStyle, minWidth: 150 }} />
            </label>
            <div style={{ fontSize: 13 }}>Paket (določi administrator)
              <div style={{ ...inputStyle, minWidth: 150, background: '#eee', borderRadius: 4 }}>
                {profile.smsPackage === 'maxi' ? 'Maxi (1200 SMS)' : profile.smsPackage === 'mini' ? 'Mini (500 SMS)' : profile.smsPackage === 'test' ? 'Testni (10 SMS)' : '— ni dodeljen —'}
              </div>
            </div>
          </div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Privzeta sporočila po kategoriji (do 100 znakov)</div>
          <label style={{ display: 'block', fontSize: 13 }}>🚗 Avtomobili (navadni)
            <textarea value={profile.defaultSmsMessage} maxLength={(profile.maxMessageLength || MAX_MESSAGE_DEFAULT)} rows={2} onChange={(e) => setProfile({ ...profile, defaultSmsMessage: e.target.value })} placeholder="npr. Pozdravljeni, zanima nas vaš avto. Pokličite nas." style={{ ...inputStyle, width: '100%' }} />
          </label>
          <label style={{ display: 'block', fontSize: 13, marginTop: 8 }}>🔧 V okvari (odkup)
            <textarea value={profile.damageSmsMessage} maxLength={(profile.maxMessageLength || MAX_MESSAGE_DEFAULT)} rows={2} onChange={(e) => setProfile({ ...profile, damageSmsMessage: e.target.value })} placeholder="npr. Pozdravljeni, odkupimo vaše poškodovano vozilo. Pokličite nas." style={{ ...inputStyle, width: '100%' }} />
          </label>
          <label style={{ display: 'block', fontSize: 13, marginTop: 8 }}>🥇 1. lastnik
            <textarea value={profile.firstOwnerSmsMessage} maxLength={(profile.maxMessageLength || MAX_MESSAGE_DEFAULT)} rows={2} onChange={(e) => setProfile({ ...profile, firstOwnerSmsMessage: e.target.value })} placeholder="npr. Pozdravljeni, zanima nas vaš lepo ohranjen avto (1. lastnik). Pokličite nas." style={{ ...inputStyle, width: '100%' }} />
          </label>
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            Spremenljivke: <code>{'{znamka} {model} {cena} {letnik} {km} {lokacija}'}</code> (samodejno vstavi podatke avta)
          </div>
          <div style={{ marginTop: 8 }}>
            <button onClick={saveProfile} style={{ padding: '8px 16px' }}>Shrani profil</button>
            <span style={{ marginLeft: 10, color: '#007AFF' }}>{profileStatus}</span>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Testno pošiljanje</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Testna št. (npr. 031377100)" style={{ ...inputStyle, minWidth: 220 }} />
            <button onClick={handleTestSend} style={{ padding: '8px 16px' }}>Pošlji test</button>
            <span style={{ color: '#555' }}>{testStatus}</span>
          </div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Pošlje privzeto sporočilo na testno številko.</div>
        </div>

        {/* POPOLNOMA Avtomatsko — auto-send SMS to the seller whenever a car matches the saved filter */}
        <div style={{ ...cardStyle, background: '#f7fbff', border: '1px solid #bcd8f0' }}>
          <div style={{ fontWeight: 700 }}>POPOLNOMA Avtomatsko</div>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 10 }}>
            Ko tvoj shranjeni filter zajame nov avto izbrane kategorije, samodejno pošljemo SMS prodajalcu
            (sporočilo te kategorije). Deluje 24/7, upošteva dnevne/mesečne limite paketa.
          </div>
          {[
            { field: 'autoSmsEnabled', label: '🚗 Avtomobili (navadni)', msg: 'defaultSmsMessage' },
            { field: 'autoSmsDamage', label: '🔧 V okvari (odkup)', msg: 'damageSmsMessage' },
            { field: 'autoSmsFirstOwner', label: '🥇 1. lastnik', msg: 'firstOwnerSmsMessage' },
          ].map((row) => (
            <div key={row.field} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid #e6eef6' }}>
              <div style={{ flex: 1, minWidth: 180, fontWeight: 600 }}>{row.label}</div>
              {profile[row.field] && !profile[row.msg] ? (
                <span style={{ fontSize: 12, color: '#a60' }}>⚠ manjka sporočilo za to kategorijo</span>
              ) : null}
              <button onClick={() => toggleAuto(row.field, row.label)} style={{ padding: '8px 18px', fontWeight: 700, border: 'none', borderRadius: 6, color: 'white', background: profile[row.field] ? '#080' : '#999' }}>
                {profile[row.field] ? 'VKLOPLJENO' : 'IZKLOPLJENO'}
              </button>
            </div>
          ))}
          {(profile.autoSmsEnabled || profile.autoSmsDamage || profile.autoSmsFirstOwner) && (!profile.companyName || !profile.contactPhone || !profile.smsPackage) ? (
            <div style={{ marginTop: 10, fontSize: 13, color: '#a60' }}>
              ⚠ Za samodejno pošiljanje potrebuješ: ime avtohiše, telefon in dodeljen paket. Preveri profil zgoraj.
            </div>
          ) : null}
          <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
            Pogoj: filter mora biti <b>aktiven</b> (nastavitev filtra). SMS se pošljejo le za nove zajete avtomobile izbrane kategorije.
          </div>
        </div>

        {/* Zaščita pred podvojenim pošiljanjem: dedup + ročna blok-lista */}
        <div style={{ ...cardStyle, background: '#fffdf5', border: '1px solid #eadfb0' }}>
          <div style={{ fontWeight: 700 }}>Zaščita pred podvojenim pošiljanjem</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 0' }}>
            <div style={{ flex: 1, minWidth: 200, fontWeight: 600 }}>
              Ne pošiljaj že poslanim številkam
              <div style={{ fontSize: 12, color: '#888', fontWeight: 400 }}>Preskoči prejemnike, ki so ti že enkrat prejeli SMS (ročno, skupinsko ali samodejno).</div>
            </div>
            <button onClick={() => toggleAuto('smsDedupEnabled', 'Brez podvojenih')} style={{ padding: '8px 18px', fontWeight: 700, border: 'none', borderRadius: 6, color: 'white', background: profile.smsDedupEnabled ? '#080' : '#999' }}>
              {profile.smsDedupEnabled ? 'VKLOPLJENO' : 'IZKLOPLJENO'}
            </button>
          </div>
          <div style={{ borderTop: '1px solid #eadfb0', paddingTop: 10, marginTop: 2 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Blok-lista številk</div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Ena številka na vrstico. Tem se SMS <b>nikoli</b> ne pošlje (ročno, skupinsko ali samodejno).</div>
            <textarea value={profile.smsBlockList} rows={4} onChange={(e) => setProfile({ ...profile, smsBlockList: e.target.value })} onBlur={saveBlockList} placeholder={'031 123 456\n040 987 654'} style={{ ...inputStyle, width: '100%', fontFamily: 'monospace' }} />
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{blockedSet.size} blokiranih številk · shrani se ob kliku izven polja</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button onClick={() => setMode('manual')} style={{ padding: '8px 16px', fontWeight: mode === 'manual' ? 700 : 400, background: mode === 'manual' ? '#007AFF' : '#eee', color: mode === 'manual' ? 'white' : '#333', border: 'none', borderRadius: 6 }}>Ročno (po enega)</button>
          <button onClick={() => setMode('auto')} style={{ padding: '8px 16px', fontWeight: mode === 'auto' ? 700 : 400, background: mode === 'auto' ? '#007AFF' : '#eee', color: mode === 'auto' ? 'white' : '#333', border: 'none', borderRadius: 6 }}>Avtomatsko (masovno)</button>
        </div>

        {loading ? <Spinner /> : null}

        {!loading && mode === 'manual' && cars.length === 0 ? <p>Ni oglasov s telefonsko številko.</p> : null}

        {!loading && mode === 'manual' && cars.length > 0 ? (
          <div>
            <div className="sms-toolbar" style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 5 }}>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Išči (znamka/model/telefon)…" style={{ padding: 7, minWidth: 220 }} />
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ padding: 7 }} title="Razvrsti">
                <option value="znamka">Razvrsti: znamka</option>
                <option value="cena">Razvrsti: cena ↑</option>
                <option value="letnik">Razvrsti: letnik (novejši)</option>
              </select>
              <span style={{ color: '#888', fontSize: 13 }}>Prikazanih {displayedCars.length} / {cars.length}</span>
              <span style={{ flex: 1 }} />
              <span>Izbranih: <b>{selected.size}</b></span>
              <button onClick={() => setSelected(new Set(displayedCars.map((c) => c.id)))} style={{ padding: '6px 12px' }}>Izberi prikazane</button>
              <button onClick={() => setSelected(new Set())} style={{ padding: '6px 12px' }}>Počisti</button>
              <button onClick={handleSelectedSend} disabled={selected.size === 0} style={{ padding: '8px 16px', fontWeight: 700 }}>Pošlji izbranim ({selected.size})</button>
              {bulkSummary(selResult)}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {displayedCars.length === 0 ? <p style={{ color: '#888' }}>Ni zadetkov za „{search}".</p> : null}
              {displayedCars.map((car) => {
                const message = messages[car.id] !== undefined ? messages[car.id] : (profile.defaultSmsMessage || '');
                const resolved = applyTemplate(message, car); // {znamka} … → dejanski podatki
                const composed = composeSms(profile.companyName, profile.contactPhone, resolved);
                const over = composed.length > (profile.maxSmsLength || MAX_SMS_DEFAULT);
                const status = sendStatus[car.id];
                const pStatus = phoneStatus(car.phone);
                const blockedNow = pStatus && pStatus.kind === 'blocked';
                const dupBlocked = pStatus && pStatus.kind === 'sent' && profile.smsDedupEnabled;
                return (
                  <div key={car.id} className="sms-car-row" style={{ display: 'flex', gap: 16, alignItems: 'flex-start', border: '1px solid #e0e0e0', borderRadius: 8, padding: 12, background: selected.has(car.id) ? '#f3f8ff' : 'white' }}>
                    <input type="checkbox" checked={selected.has(car.id)} onChange={() => toggleSelect(car.id)} style={{ marginTop: 5 }} />
                    <div className="sms-car-info" style={{ flex: '0 0 220px' }}>
                      <div style={{ fontWeight: 600 }}>{car.znamka} {car.model}</div>
                      <div style={{ color: '#555', fontSize: 13 }}>{car.cena ? `${car.cena} €` : ''} {car.letnik || ''} · {car.viewLocation || ''}</div>
                      <div style={{ marginTop: 4, fontWeight: 600, color: '#007AFF' }}>📞 {car.phone}</div>
                      {pStatus ? (
                        <div style={{ marginTop: 4, fontSize: 12, fontWeight: 700, padding: '2px 6px', borderRadius: 4, display: 'inline-block',
                          background: blockedNow ? '#fdecec' : '#fff5e6', color: blockedNow ? '#c0392b' : '#a66500' }}>
                          {blockedNow ? '⛔ na blok listi' : `✓ že poslano${pStatus.date ? ' (' + String(pStatus.date).slice(0, 10) + ')' : ''}`}
                        </div>
                      ) : null}
                    </div>
                    <div style={{ flex: 1 }}>
                      <textarea value={message} maxLength={(profile.maxMessageLength || MAX_MESSAGE_DEFAULT)} rows={2} onChange={(e) => setMessages((m) => ({ ...m, [car.id]: e.target.value }))} placeholder="Sporočilo" style={{ ...inputStyle, width: '100%', resize: 'vertical' }} />
                      <div style={{ fontSize: 12, color: '#888' }}>
                        Sporočilo: {message.length}/{(profile.maxMessageLength || MAX_MESSAGE_DEFAULT)} · SMS: <span style={{ color: over ? '#d00' : '#888' }}>{composed.length}/{(profile.maxSmsLength || MAX_SMS_DEFAULT)}</span>
                      </div>
                      {status ? <div style={{ fontSize: 12, marginTop: 4, color: status.type === 'error' ? '#d00' : status.type === 'ok' ? '#080' : '#a60' }}>{status.text}</div> : null}
                    </div>
                    <button className="sms-send-btn" onClick={() => handleSend(car)} disabled={!message || over || (status && status.type === 'sending') || blockedNow || dupBlocked} title={blockedNow ? 'Na blok listi' : dupBlocked ? 'Že poslano tej številki' : ''} style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>Pošlji SMS</button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {!loading && mode === 'auto' ? (
          <div style={cardStyle}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Kategorija vozil</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              {[
                { key: 'avtomobili', label: '🚗 Avtomobili', n: cars.length },
                { key: 'okvari', label: '🔧 V okvari', n: damagedCars.length },
                { key: 'lastnik', label: '🥇 1. lastnik', n: firstOwnerCars.length },
              ].map((c) => (
                <button key={c.key} onClick={() => setSmsCategory(c.key)} style={{ padding: '8px 16px', fontWeight: smsCategory === c.key ? 700 : 400, background: smsCategory === c.key ? '#007AFF' : '#eee', color: smsCategory === c.key ? 'white' : '#333', border: 'none', borderRadius: 6 }}>
                  {c.label} ({c.n})
                </button>
              ))}
            </div>
            {!categoryMessage ? (
              <div style={{ fontSize: 13, color: '#a60', marginBottom: 10 }}>⚠ Za to kategorijo ni nastavljenega sporočila (profil zgoraj).</div>
            ) : null}
            <div style={{ display: 'flex', gap: 20, marginBottom: 12 }}>
              <label><input type="radio" checked={autoTarget === 'all'} onChange={() => setAutoTarget('all')} /> Pošlji vsem</label>
              <label><input type="radio" checked={autoTarget === 'filter'} onChange={() => setAutoTarget('filter')} /> Po filtru</label>
            </div>

            {autoTarget === 'filter' ? (
              <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  Znamke (več izbir){filters.brands.length ? ` – izbranih ${filters.brands.length}` : ''}
                </div>
                <input value={brandSearch} onChange={(e) => setBrandSearch(e.target.value)} placeholder="Išči znamko ..." style={{ ...inputStyle, width: 220, marginBottom: 6 }} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, maxHeight: 150, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6, padding: 8 }}>
                  {visibleBrands.map((b) => (
                    <label key={b} style={{ fontSize: 13, border: '1px solid #ddd', borderRadius: 14, padding: '3px 10px', background: filters.brands.includes(b) ? '#eef5ff' : 'white' }}>
                      <input type="checkbox" checked={filters.brands.includes(b)} onChange={() => toggleIn('brands', b)} /> {b}
                    </label>
                  ))}
                  {visibleBrands.length === 0 ? <span style={{ color: '#888' }}>Ni zadetkov.</span> : null}
                </div>

                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Gorivo</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  {Object.entries(FUEL_TYPES).map(([code, name]) => (
                    <label key={code} style={{ fontSize: 13, border: '1px solid #ddd', borderRadius: 14, padding: '3px 10px', background: filters.fuels.includes(code) ? '#eef5ff' : 'white' }}>
                      <input type="checkbox" checked={filters.fuels.includes(code)} onChange={() => toggleIn('fuels', code)} /> {name}
                    </label>
                  ))}
                </div>

                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Karoserija</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  {Object.entries(BODY_TYPES).map(([code, name]) => (
                    <label key={code} style={{ fontSize: 13, border: '1px solid #ddd', borderRadius: 14, padding: '3px 10px', background: filters.bodies.includes(code) ? '#eef5ff' : 'white' }}>
                      <input type="checkbox" checked={filters.bodies.includes(code)} onChange={() => toggleIn('bodies', code)} /> {name}
                    </label>
                  ))}
                </div>

                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Tip prodajalca</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  {Object.entries(SELLER_TYPES).map(([code, name]) => {
                    const disabled = code === 'Trgovec'; // filtriramo samo fizicne osebe
                    return (
                    <label key={code} title={disabled ? 'Filtriramo samo fizične osebe' : undefined} style={{ fontSize: 13, border: '1px solid #ddd', borderRadius: 14, padding: '3px 10px', background: (!disabled && filters.sellers.includes(code)) ? '#eef5ff' : 'white', opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
                      <input type="checkbox" checked={!disabled && filters.sellers.includes(code)} disabled={disabled} onChange={() => { if (!disabled) toggleIn('sellers', code); }} /> {name}
                    </label>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {[['Letnik', 'letnikMin', 'letnikMax'], ['Cena (€)', 'cenaMin', 'cenaMax'], ['Prevoženi km', 'kmMin', 'kmMax']].map((row) => (
                    <div key={row[0]} style={{ fontSize: 13 }}>
                      <div>{row[0]}</div>
                      <input type="number" value={filters[row[1]]} onChange={(e) => setFilters({ ...filters, [row[1]]: e.target.value })} placeholder="od" style={{ ...inputStyle, width: 90 }} />
                      {' – '}
                      <input type="number" value={filters[row[2]]} onChange={(e) => setFilters({ ...filters, [row[2]]: e.target.value })} placeholder="do" style={{ ...inputStyle, width: 90 }} />
                    </div>
                  ))}
                  <div style={{ fontSize: 13 }}>
                    <div>Lokacija</div>
                    <select value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })} style={inputStyle}>
                      {REGIONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
                  <button onClick={saveSmsFilter} style={{ padding: '10px 18px', fontWeight: 700, background: '#007AFF', color: 'white', border: 'none', borderRadius: 6 }}>Potrdi filter</button>
                  <button onClick={clearSmsFilter} style={{ padding: '10px 18px', background: '#eee', color: '#333', border: 'none', borderRadius: 6 }}>Razveljavi filter</button>
                  {filterSaved ? <span style={{ color: '#1f9d57', fontWeight: 600 }}>✓ Filter shranjen</span> : null}
                </div>
              </div>
            ) : null}

            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button onClick={handleBulkSend} disabled={bulkSending} style={{ padding: '10px 20px', fontWeight: 700 }}>
                {bulkSending ? 'Pošiljam ...' : `Pošlji vsem (${filteredCars.length})`}
              </button>
              <span style={{ color: '#555' }}>Sporočilo: privzeto sporočilo izbrane kategorije (profil).</span>
            </div>

            {bulkResult ? <div style={{ marginTop: 12, fontSize: 14 }}>{bulkSummary(bulkResult)}</div> : null}
          </div>
        ) : null}

        {/* Sending history with delivery status */}
        {!loading && history.length > 0 ? (
          <div style={{ ...cardStyle, marginTop: 24 }}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>Zgodovina pošiljanja (zadnjih {history.length})</div>
            <div className="sms-history-scroll">
            <table className="sms-history-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd', color: '#555' }}>
                  <th style={{ padding: 6 }}>Čas</th>
                  <th style={{ padding: 6 }}>Prejemnik</th>
                  <th style={{ padding: 6 }}>Vozilo</th>
                  <th style={{ padding: 6 }}>Sporočilo</th>
                  <th style={{ padding: 6 }}>Način / filter</th>
                  <th style={{ padding: 6 }}>Oddano</th>
                  <th style={{ padding: 6 }}>Dostava</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => {
                  const ds = h.deliveryStatus;
                  const dsColor = ds === 'DELIVERED' ? '#080' : (ds === 'EXPIRED' || ds === 'BLOCKED' || ds === 'SUBSCRIBER_UNKNOWN') ? '#d00' : '#a60';
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: 6, whiteSpace: 'nowrap' }}>{h.sentAt ? new Date(h.sentAt).toLocaleString('sl-SI') : ''}</td>
                      <td style={{ padding: 6 }}>{h.recipient}</td>
                      <td style={{ padding: 6, maxWidth: 240, fontSize: 12 }}>
                        {h.vehicleInfo
                          ? (h.avtonetId
                              ? <a href={`https://www.avto.net/Ads/details.asp?id=${h.avtonetId}`} target="_blank" rel="noreferrer">{h.vehicleInfo}</a>
                              : h.vehicleInfo)
                          : '—'}
                      </td>
                      <td style={{ padding: 6, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.message}</td>
                      <td style={{ padding: 6, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#555' }}>{h.filterUsed || '—'}</td>
                      <td style={{ padding: 6, color: h.sendStatus === 'OK' ? '#080' : '#a60' }}>{h.sendStatus}</td>
                      <td style={{ padding: 6, color: dsColor }}>{ds || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Dostavni status posodobi Telemach (Kurir Notify): DELIVERED / EXPIRED / BLOCKED …</div>
          </div>
        ) : null}
      </div>
      <Toast message={toast && toast.message} type={toast && toast.type} onClose={() => setToast(null)} />
    </div>
  );
}

export default SmsBoard;
